import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, SectionHeader } from '@/components/ui/card';
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/field';
import { EmptyState, ListSkeleton, Spinner, TableSkeleton } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import { SortableTH, Table, TableWrap, TBody, TD, THead, TR, useServerSort, TablePagination} from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { adminStaffPageQuery, queryKeys } from '@/lib/queries';
import { errorMessage, tabfile } from '@/lib/api';
import { formatRelative } from '@/lib/format';

/** Rows per page, matching the portal's tables. */
const PAGE_SIZE = 10;

export const Route = createFileRoute('/_app/admin/staff')({
  // The shape this page takes, rather than a search box over a table for
  // every route: the screen must not rearrange itself once the data lands.
  pendingComponent: () => <ListSkeleton rows={6} columns={4} />,
  loader: ({ context }) => context.queryClient.ensureQueryData(adminStaffPageQuery(1, PAGE_SIZE)),
  component: AdminStaffPage,
});

const statusCopy: Record<string, { label: string; tone: 'success' | 'info' | 'neutral' }> = {
  active: { label: 'Active', tone: 'success' },
  invited: { label: 'Invited', tone: 'info' },
  disabled: { label: 'Disabled', tone: 'neutral' },
};

function AdminStaffPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  // The column headers order the whole list, not the page on screen.
  const { sort, onSort, param: sortParam } = useServerSort(null, () => setPage(1));
  const staff = useQuery(adminStaffPageQuery(page, PAGE_SIZE, sortParam));
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');

  const invite = useMutation({
    mutationFn: () => tabfile.POST('/v1/admin/staff', { body: { email: email.trim() } }),
    onSuccess: () => {
      toast.success('Invitation sent', `${email.trim()} can now set a password and sign in as staff.`);
      setEmail('');
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminStaff });
    },
    onError: (error) => toast.error('Could not send the invitation', errorMessage(error)),
  });

  const items = staff.data?.items ?? [];


  return (
    <Card data-tour="admin-staff">
      <SectionHeader
        title="Staff"
        description="Everybody at Tab Service who can sign in to the admin area. Staff see every customer, so invite only the people who support them."
        actions={
          <Button data-tour="admin-invite-staff" onClick={() => setOpen(true)}>
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            Invite staff
          </Button>
        }
      />

      {staff.isPending ? (
        <TableSkeleton rows={4} columns={3} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No staff accounts yet"
          description="The first accounts come from the staff bootstrap setting. Invite the rest here."
          action={<Button onClick={() => setOpen(true)}>Invite staff</Button>}
        />
      ) : (
        <>
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <SortableTH columnKey="email" sort={sort} onSort={onSort}>
                    Email
                  </SortableTH>
                  <SortableTH columnKey="status" sort={sort} onSort={onSort}>
                    Status
                  </SortableTH>
                  <SortableTH columnKey="lastSignIn" sort={sort} onSort={onSort}>
                    Last signed in
                  </SortableTH>
                </tr>
              </THead>
              <TBody>
                {items.map((item) => {
                  const status = statusCopy[item.status] ?? statusCopy.disabled;
                  return (
                    <TR key={item.id}>
                      <TD className="font-medium">{item.email}</TD>
                      <TD>
                        <Pill tone={status?.tone ?? 'neutral'}>{status?.label ?? item.status}</Pill>
                      </TD>
                      <TD className="text-muted-foreground">
                        {item.lastSignInAt ? formatRelative(item.lastSignInAt) : 'never'}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </TableWrap>
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            total={staff.data?.total ?? staff.data?.items?.length ?? 0}
            busy={staff.isFetching}
            onPageChange={setPage}
          />
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          title="Invite staff"
          description="They receive an email to set a password. Staff can read every customer on Tab Service."
          footer={
            <>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                variant="primary"
                disabled={!/.+@.+\..+/.test(email.trim()) || invite.isPending}
                onClick={() => invite.mutate()}
              >
                {invite.isPending ? <Spinner /> : null}
                Send invitation
              </Button>
            </>
          }
        >
          <Field label="Email" hint="A Tab Service address.">
            {(props) => (
              <Input
                {...props}
                type="email"
                value={email}
                autoFocus
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@tabservice.net"
              />
            )}
          </Field>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
